/**
 * Client-side validator for the PeopleSoft enrollment CSV (which is also
 * the schema of the fix CSV: course_id,user_id,role,section_id,status).
 *
 * Used by the New-sync page (validating uploads before they hit S3) and
 * the Manual-upload page (validating before sending to Canvas).
 *
 * Errors fail the file; warnings let the user proceed but call attention
 * to unusual values (e.g., a role or status outside the well-known set).
 */

export interface CsvIssue {
  /** 1-indexed row number (header is row 1, first data row is row 2). null for file-level issues. */
  row: number | null;
  message: string;
}

export interface CsvValidationResult {
  valid: boolean;
  dataRows: number;
  headerOk: boolean;
  errors: CsvIssue[];
  warnings: CsvIssue[];
}

export const EXPECTED_CSV_HEADER =
  '"course_id","user_id","role","section_id","status"';

/** Well-known Canvas role names that PeopleSoft exports use. */
const VALID_ROLES = new Set([
  "Student",
  "Teacher",
  "TA",
  "Observer",
  "Designer",
]);

/** Well-known Canvas enrollment statuses from PeopleSoft. */
const VALID_STATUSES = new Set(["Active", "Inactive", "Completed", "Deleted"]);

/** Cap issue lists so the UI stays manageable on huge files. */
const MAX_ISSUES_REPORTED = 25;

export function validateEnrollmentsCsv(csv: string): CsvValidationResult {
  const errors: CsvIssue[] = [];
  const warnings: CsvIssue[] = [];

  const rawLines = csv.split(/\r?\n/);
  // Keep blank-line awareness for row numbers, but skip them for actual
  // parsing — PeopleSoft sometimes adds a trailing newline.
  const lines: { text: string; lineNumber: number }[] = [];
  rawLines.forEach((l, i) => {
    if (l.trim().length > 0) lines.push({ text: l, lineNumber: i + 1 });
  });

  if (lines.length === 0) {
    return {
      valid: false,
      dataRows: 0,
      headerOk: false,
      errors: [{ row: null, message: "The file is empty." }],
      warnings: [],
    };
  }

  const headerOk = lines[0]!.text === EXPECTED_CSV_HEADER;
  if (!headerOk) {
    errors.push({
      row: lines[0]!.lineNumber,
      message:
        "Header row doesn't match the expected format. " +
        `Expected exactly: ${EXPECTED_CSV_HEADER}`,
    });
  }

  const dataLines = lines.slice(1);
  if (dataLines.length === 0) {
    errors.push({
      row: null,
      message: "The file has a header but no data rows.",
    });
    return {
      valid: false,
      dataRows: 0,
      headerOk,
      errors: capIssues(errors),
      warnings: capIssues(warnings),
    };
  }

  for (const { text, lineNumber } of dataLines) {
    if (
      errors.length >= MAX_ISSUES_REPORTED &&
      warnings.length >= MAX_ISSUES_REPORTED
    ) {
      break;
    }
    const fields = parseQuotedFields(text);
    if (!fields) {
      errors.push({
        row: lineNumber,
        message:
          `Row ${lineNumber}: couldn't parse fields. ` +
          `Each value must be wrapped in double quotes.`,
      });
      continue;
    }
    if (fields.length !== 5) {
      errors.push({
        row: lineNumber,
        message: `Row ${lineNumber}: expected 5 quoted fields, found ${fields.length}.`,
      });
      continue;
    }

    const [course_id, user_id, role, section_id, status] = fields as [
      string,
      string,
      string,
      string,
      string,
    ];

    if (!course_id.trim()) {
      errors.push({
        row: lineNumber,
        message: `Row ${lineNumber}: course_id is empty.`,
      });
    }
    if (!section_id.trim()) {
      errors.push({
        row: lineNumber,
        message: `Row ${lineNumber}: section_id is empty.`,
      });
    }
    if (!user_id.trim()) {
      errors.push({
        row: lineNumber,
        message: `Row ${lineNumber}: user_id is empty.`,
      });
    } else if (!/^\d+$/.test(user_id.trim())) {
      warnings.push({
        row: lineNumber,
        message:
          `Row ${lineNumber}: user_id "${user_id}" doesn't look like a number. ` +
          `PeopleSoft IDs are usually all digits.`,
      });
    }
    if (!role.trim()) {
      errors.push({
        row: lineNumber,
        message: `Row ${lineNumber}: role is empty.`,
      });
    } else if (!VALID_ROLES.has(role)) {
      warnings.push({
        row: lineNumber,
        message:
          `Row ${lineNumber}: role "${role}" is unusual. ` +
          `Expected one of: ${[...VALID_ROLES].join(", ")}.`,
      });
    }
    if (!status.trim()) {
      errors.push({
        row: lineNumber,
        message: `Row ${lineNumber}: status is empty.`,
      });
    } else if (!VALID_STATUSES.has(status)) {
      warnings.push({
        row: lineNumber,
        message:
          `Row ${lineNumber}: status "${status}" is unusual. ` +
          `Expected one of: ${[...VALID_STATUSES].join(", ")}.`,
      });
    }
  }

  return {
    valid: errors.length === 0,
    dataRows: dataLines.length,
    headerOk,
    errors: capIssues(errors),
    warnings: capIssues(warnings),
  };
}

function capIssues(items: CsvIssue[]): CsvIssue[] {
  if (items.length <= MAX_ISSUES_REPORTED) return items;
  const remaining = items.length - MAX_ISSUES_REPORTED;
  return [
    ...items.slice(0, MAX_ISSUES_REPORTED),
    { row: null, message: `…and ${remaining} more.` },
  ];
}

/**
 * Match every "…" sequence on the line. PeopleSoft exports don't use
 * escaped quotes inside field values, so a simple regex is enough.
 */
function parseQuotedFields(line: string): string[] | null {
  const matches = line.match(/"([^"]*)"/g);
  if (!matches) return null;
  return matches.map((m) => m.slice(1, -1));
}
